const __PLUS_API_BASE_URL__ = "/api/plus"

/**
 * Common types
 */
export type SurveyType = "None" | "HomebuyersReport" | "BuildingSurvey"

export type PlusQuoteType = SurveyType | "HomebuyersReportWithValuation"

/**
 * Eligibility
 */
export interface PlusJourneyEligibility {
  eligibleForPlus: boolean
  eligibleForSelfServePlus: boolean
  hasFoundSolicitor: boolean
}

/**
 * Contract and payment journey
 */
export interface Signature {
  text: string
  timestamp: string
}

interface CommonAppState {
  currentApplicant: Applicant
  journeyState: JourneyState
  quote: number
  surveyType: SurveyType
  sellingProperty: boolean
}

export type AppState =
  | ({ tag: "Single" } & CommonAppState)
  | ({ tag: "Joint"; otherApplicant: Applicant } & CommonAppState)

export interface Applicant {
  forename: string
  surname: string
  email: string
  signature: Signature | null
}

export type JourneyState =
  | {
      tag: JourneyStateTag.ReviewingContract
      contractStep: ContractStep
      paymentTimestamp: string | null
      contractVariant?: ContractVariant
    }
  | {
      tag: JourneyStateTag.SigningContract
      paymentTimestamp: string | null
      contractVariant?: ContractVariant
    }
  | { tag: JourneyStateTag.Paying; contractVariant?: ContractVariant }
  | {
      tag: JourneyStateTag.Done
      paymentTimestamp: string
      contractVariant?: ContractVariant
    }

export enum JourneyStateTag {
  ReviewingContract = "ReviewingContract",
  SigningContract = "SigningContract",
  Paying = "Paying",
  Done = "Done",
}

export enum ContractStep {
  WhatIsHabitoPlusStep = "WhatIsHabitoPlusStep",
  WhatFeesStep = "WhatFeesStep",
  CollectingDataStep = "CollectingDataStep",
  DisclaimerStep = "DisclaimerStep",
  CommunicationsStep = "CommunicationsStep",
  ComplaintsStep = "ComplaintsStep",
  GeneralStep = "GeneralStep",
  FeeStep = "FeeStep",
  SurveyorStep = "SurveyorStep",
  DataStep = "DataStep",
  WaiverStep = "WaiverStep",
  ProtectionsStep = "ProtectionsStep",
  GoWrongStep = "GoWrongStep",
}

export const contractSteps2020 = [
  ContractStep.FeeStep,
  ContractStep.SurveyorStep,
  ContractStep.DataStep,
  ContractStep.WaiverStep,
  ContractStep.ProtectionsStep,
  ContractStep.GoWrongStep,
]

export const contractSteps2023 = [
  ContractStep.WhatIsHabitoPlusStep,
  ContractStep.WhatFeesStep,
  ContractStep.CollectingDataStep,
  ContractStep.DisclaimerStep,
  ContractStep.CommunicationsStep,
  ContractStep.ComplaintsStep,
  ContractStep.GeneralStep,
]

export enum ContractVariant {
  ContractVariant2020 = "2020",
  ContractVariant2023 = "2023",
}

/**
 * Self-serve quote journey
 */
export interface PlusQuoteBreakdown {
  serviceFee: number
  landRegistryFee: number
  purchaseLegalFee: number
  quoteAmount: number
  saleLegalFee: number
  surveyCost: number
  surveyAndValuationCost: number
  valuationCost: number | null
  customerVatIncludedInQuote?: number | null
}

export type PlusQuoteBreakdownOption = keyof PlusQuoteBreakdownOptions

export interface PlusQuoteBreakdownOptions {
  noSurvey?: PlusQuoteBreakdown
  homebuyersReport: PlusQuoteBreakdown
  homebuyersReportWithValuation: PlusQuoteBreakdown
  buildingSurvey: PlusQuoteBreakdown
}

/**
 * Plus quote journey
 */
export type PlusQuoteJourneyId = string
export type PlusQuoteId = string

export type TenureType = "freehold" | "leasehold"

export interface NTCDataNeededForQuote {
  forename: string
  surname: string
  secondaryForename: string | null
  secondarySurname: string | null
}

export interface NTCPlusFormAnswers extends PlusFormAnswers {
  mortgageType: "Residential" | "BTL"
}

export interface PlusFormAnswers {
  isFirstTimeBuyer?: boolean
  isUsingScheme: boolean
  purchasePropertyInEnglandAndWales: boolean
  purchasePropertyValue: number
  purchaseTenureType: TenureType
  purchaseWhetherNewBuild: "yes" | "no" | "notSure"
  salePropertyDetails: {
    salePropertyValue: number
    saleTenureType: TenureType
  } | null
}

export interface PlusQuoteDataFromForm {
  eligibilityInputs: {
    purchasePropertyInEnglandAndWales: boolean
    usingSchemeOrPlanningToLet: boolean
  }
  customerDetails: null | {
    primaryForename: string
    primarySurname: string
    secondaryForename: string | null
    secondarySurname: string | null
  }
  propertyDetails: {
    purchasePropertyValue: number
    purchaseTenureType: TenureType
    purchaseWhetherNewBuild: "yes" | "no" | "notSure"
    salePropertyDetails: {
      salePropertyValue: number
      saleTenureType: TenureType
    } | null
  }
}

export interface PlusQuoteOptions {
  noSurvey: PlusQuoteOption | null
  homebuyersReport: PlusQuoteOption
  homebuyersReportWithValuation: PlusQuoteOption
  buildingSurvey: PlusQuoteOption
}

export interface PlusQuoteOption {
  quoteId: PlusQuoteId
  quoteAmount: number
  quoteBreakdown: {
    purchaseLegalsWorth: number
    purchaseSurveyWorth: number
    purchaseLandRegistryCost: number
    saleLegalsWorth: number | null
    plusServiceFee: number
    customerVatIncludedInQuote: number
    valuationCost: number | null
  }
}

export interface PlusQuote extends PlusQuoteDataFromForm {
  issuedAt: string
  quoteOptions: PlusQuoteOptions
}

export type PlusQuoteStage =
  | "not-started"
  | "ineligible"
  | "deferred"
  | "created"
  | "selected"
  | "accepted"

export type ViaOptions = "ntc-plus" | "homebuying"

export interface PlusQuoteJourney {
  journeyId: PlusQuoteJourneyId
  quotes: PlusQuote[]
  selectedQuote: PlusQuoteId | null
  optedInQuote: PlusQuoteId | null
  associatedApplicationJourney: string | null
  stage: PlusQuoteStage
  lastInteractedVia: ViaOptions | null
}

export const createPlusQuote = async (
  authToken: string,
  journeyId: PlusQuoteJourneyId,
  form: PlusQuoteDataFromForm,
  via: ViaOptions
): Promise<PlusQuoteJourney> => {
  const res = await fetch(
    `${__PLUS_API_BASE_URL__}/quote-journey/${journeyId}/create-quote?via=${via}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(form),
    }
  )

  if (res.status !== 200) {
    const body = await res.text()
    throw new Error("Error calling createPlusQuote" + res + " | " + body)
  }
  return await res.json()
}

export const fetchPlusQuoteSummary = async (
  authToken: string,
  via: ViaOptions
): Promise<PlusQuoteJourney> => {
  const res = await fetch(
    `${__PLUS_API_BASE_URL__}/quote-journey/summary?via=${via}`,
    {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    }
  )

  if (res.status !== 200) {
    const body = await res.text()
    throw new Error("error calling quote-journey/summary:" + body)
  }
  return await res.json()
}

export const downloadPlusQuote = async (
  token: string,
  quoteId: PlusQuoteId,
  via: ViaOptions
): Promise<Blob> => {
  const res = await fetch(
    `${__PLUS_API_BASE_URL__}/quote-journey/pdf/${quoteId}?via=${via}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (res.status !== 200) {
    const body = await res.text()
    throw new Error(
      "error calling quote-journey/pdf:" + `quoteId: ${quoteId}; ` + body
    )
  }

  return await res.blob()
}

export const optIntoPlusQuote = async (
  token: string,
  journeyId: PlusQuoteJourneyId,
  quoteId: PlusQuoteId,
  via: ViaOptions
): Promise<PlusQuoteJourney> => {
  const res = await fetch(
    `${__PLUS_API_BASE_URL__}/quote-journey/${journeyId}/opt-in?via=${via}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ quoteId }),
    }
  )

  if (res.status !== 200) {
    const body = await res.text()
    throw new Error(
      "error calling quote-journey/opt-in:" +
        `journeyId: ${journeyId}; ` +
        `quoteId: ${quoteId}; ` +
        body
    )
  }

  return await res.json()
}

export const recordDeferredPlusQuote = async (
  token: string,
  journeyId: PlusQuoteJourneyId,
  via: ViaOptions
): Promise<void> => {
  const res = await fetch(
    `${__PLUS_API_BASE_URL__}/quote-journey/${journeyId}/record-deferred?via=${via}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (res.status !== 200) {
    const body = await res.text()
    throw new Error(
      "error calling quote-journey/record-deferred:" +
        `journeyId: ${journeyId}; ` +
        body
    )
  }
}

export const recordIneligiblePlusQuote = async (
  token: string,
  journeyId: PlusQuoteJourneyId,
  via: ViaOptions
): Promise<void> => {
  const res = await fetch(
    `${__PLUS_API_BASE_URL__}/quote-journey/${journeyId}/record-ineligible?via=${via}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (res.status !== 200) {
    const body = await res.text()
    throw new Error(
      "error calling quote-journey/record-ineligible:" +
        `journeyId: ${journeyId}; ` +
        body
    )
  }
}

export const selectPlusQuote = async (
  token: string,
  journeyId: PlusQuoteJourneyId,
  quoteId: PlusQuoteId,
  via: ViaOptions
): Promise<PlusQuoteJourney> => {
  const res = await fetch(
    `${__PLUS_API_BASE_URL__}/quote-journey/${journeyId}/select-quote?via=${via}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ quoteId }),
    }
  )

  if (res.status !== 200) {
    const body = await res.text()
    throw new Error(
      "error calling quote-journey/select-quote:" +
        `journeyId: ${journeyId}; ` +
        `quoteId: ${quoteId}; ` +
        body
    )
  }

  return await res.json()
}

export const getPlusQuoteBreakdown = async (
  token: string,
  exAppId: string
): Promise<PlusQuoteBreakdown | null> => {
  const res = await fetch(
    `${__PLUS_API_BASE_URL__}/selected-quote/${exAppId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (res.status !== 200) {
    const body = await res.text()
    throw new Error("error calling getPlusQuoteBreakdown: " + body)
  }

  return await res.json()
}

export const getPlusPartialContractState = async (
  token: string,
  exAppId: string
): Promise<AppState | null> => {
  const res = await fetch(
    `${__PLUS_API_BASE_URL__}/contract-state/${exAppId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  )

  if (res.status !== 200) {
    const body = await res.text()

    throw new Error(
      "error calling getPlusPartialContractState: " + res + ", " + body
    )
  }

  const json = await res.json()

  if (json === null) {
    return null
  }

  return json
}
